import React from 'react';
import HeaderBlue from '../../components/HeaderBlue';
import FooterBlue from '../../components/FooterBlue';

const PrivacyPolicy = () => (
    <>
        <div
            className="relative h-[260px] md:h-[320px] w-full overflow-hidden"
            style={{
                backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("/assets/policyheader.webp")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            <HeaderBlue />
        </div>

         <div className="bg-gray-50 min-h-screen pt-24 font-[Oswald] text-gray-900 leading-relaxed">
            <main className="max-w-6xl mx-auto p-6 md:p-12 bg-white rounded-xl shadow-lg ">

                <div className="relative z-10 -mt-32 md:-mt-48  mx-auto mb-12">
                    <div
                        className="relative z-10 -mt-32 md:-mt-40 text-white rounded-2xl shadow-md p-10 mb-12  mx-auto"
                        style={{ backgroundColor: '#3c9bf9' }}>
                    <h1 className="text-2xl md:text-5xl font-bold mb-3">Privacy Notice</h1>
                    </div>
                    <p>
                        This privacy notice for Fleety LLC (“we,” “us,” or “our”) describes how and why we might collect, store, use, and/or share (“process”) your information when you use our services (“Services”), such as when you:
                    </p>
                    <ul className="mb-6 mt-3 list-disc ml-5">
                        <li>
                            Visit our website at{" "}
                            <a href="https://fleetycdl.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                               (https://fleetycdl.com)
                            </a>
                            , or any website of ours that links to this privacy notice.
                        </li>
                        <li>Use our software, mobile applications, or related platforms.</li>
                        <li>Engage with us in other related ways, including any sales, marketing, or events.</li>
                    </ul>
                    <p>
                        <strong>Questions or concerns?</strong> Reading this privacy notice will help you understand your privacy rights and choices. If you do not agree with our policies and practices, please do not use our Services.
                    </p>
                    <hr className="my-8" />
                    <h2 className="text-xl font-semibold mb-4">Summary of Key Points</h2>
                    <ul className="mb-6 list-disc pl-5">
                        <li>
                            <strong>What personal information do we process?</strong> We may process personal information depending on how you interact with us, the choices you make, and the products and features you use.
                        </li>
                        <li>
                            <strong>Do we process any sensitive personal information?</strong> We do not process sensitive personal information.
                        </li>
                        <li>
                            <strong>Do we receive any information from third parties?</strong> We do not receive any information from third parties.
                        </li>
                        <li>
                            <strong>How do we process your information?</strong> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent.
                        </li>
                        <li>
                            <strong>In what situations and with which parties do we share personal information?</strong> We may share information in specific situations and with specific third parties.
                        </li>
                        <li>
                            <strong>How do we keep your information safe?</strong> We use organizational and technical safeguards to protect your personal information. However, no system can be guaranteed 100% secure.
                        </li>
                        <li>
                            <strong>What are your rights?</strong> Depending on where you are located, privacy laws may give you certain rights regarding your personal information.
                        </li>
                        <li>
                            <strong>How do you exercise your rights?</strong> The easiest way is by submitting a data subject access request or contacting us at the details below.
                        </li>
                    </ul>
                    <hr className="my-8" />
                    <h2 className="text-xl font-semibold mb-4">Table of Contents</h2>
                    <ol className="mb-6 list-decimal list-inside">
                        <li>What Information Do We Collect?</li>
                        <li>How Do We Process Your Information?</li>
                        <li>What Legal Bases Do We Rely On to Process Your Information?</li>
                        <li>SMS Communications and Data Usage</li>
                        <li>When and With Whom Do We Share Your Personal Information?</li>
                        <li>How Long Do We Keep Your Information?</li>
                        <li>How Do We Keep Your Information Safe?</li>
                        <li>Do We Collect Information from Minors?</li>
                        <li>What Are Your Privacy Rights?</li>
                        <li>Controls for Do-Not-Track Features</li>
                        <li>Do United States Residents Have Specific Privacy Rights?</li>
                        <li>Do We Make Updates to This Notice?</li>
                        <li>How Can You Contact Us About This Notice?</li>
                        <li>How Can You Review, Update, or Delete the Data We Collect from You?</li>
                    </ol>
                    <hr className="my-8" />

                    {/* Section 1 */}
                    <section className="mb-8">
                        <h3 className="text-lg font-semibold mb-3">1. What Information Do We Collect?</h3>
                        <p><strong>Personal information you disclose to us.</strong> We collect personal information that you voluntarily provide when you:</p>
                        <ul className="mb-3 list-disc list-inside">
                            <li>Register for an account.</li>
                            <li>Express interest in our products or Services.</li>
                            <li>Participate in activities on the Services.</li>
                            <li>Contact us directly.</li>
                        </ul>
                        <p>Examples of personal information collected:</p>
                        <ul className="mb-3 list-disc list-inside">
                            <li>Name</li>
                            <li>Phone number</li>
                            <li>Email address</li>
                            <li>Mailing address</li>
                            <li>Contact preferences</li>
                        </ul>
                        <p>
                            We do not collect sensitive personal information.
                            <br />
                            You are responsible for providing accurate and complete information, and updating it if it changes.
                        </p>
                    </section>

                    {/* Section 2 */}
                    <section className="mb-8">
                        <h3 className="text-lg font-semibold mb-3">2. How Do We Process Your Information?</h3>
                        <p>We process personal information for reasons such as:</p>
                        <ul className="mb-3 list-disc list-inside">
                            <li>Responding to inquiries and support requests.</li>
                            <li>Requesting feedback and improving our Services.</li>
                            <li>Delivering personalized advertising or promotions.</li>
                            <li>Evaluating and improving features, campaigns, and user experience.</li>
                            <li>Protecting against fraud, security risks, or misuse of the Services.</li>
                            <li>Complying with legal obligations.</li>
                        </ul>
                    </section>

                    {/* Section 3 */}
                    <section className="mb-8">
                        <h3 className="text-lg font-semibold mb-3">3. What Legal Bases Do We Rely On to Process Your Information?</h3>
                        <p>We only process personal information when we have a valid legal basis, such as:</p>
                        <ul className="mb-3 list-disc list-inside">
                            <li>With your consent.</li>
                            <li>To comply with legal obligations.</li>
                            <li>To fulfill a contract with you.</li>
                            <li>To pursue legitimate business interests.</li>
                        </ul>
                        <p>(Canada/International users: consent rules may vary. You may withdraw consent at any time.)</p>
                    </section>

                    {/* Section 4 */}
                    <section className="mb-8">
                        <h3 className="text-lg font-semibold mb-3">4. SMS Communications and Data Usage</h3>
                        <p>By providing your mobile number, you consent to receive text messages from Fleety LLC.</p>
                        <ul className="mb-3 list-disc list-inside">
                            <li>Message and data rates may apply.</li>
                            <li>Frequency may vary.</li>
                            <li>You can opt out at any time by replying “STOP.”</li>
                            <li>SMS consent data will not be shared with third parties for marketing/promotional purposes.</li>
                        </ul>
                    </section>

                    {/* Section 5 */}
                    <section className="mb-8">
                        <h3 className="text-lg font-semibold mb-3">5. When and With Whom Do We Share Your Personal Information?</h3>
                        <p>We may share information:</p>
                        <ul className="mb-3 list-disc list-inside">
                            <li><strong>Business Transfers:</strong> During a merger, acquisition, financing, or sale of company assets.</li>
                            <li><strong>Service Providers:</strong> For hosting, payments, analytics, or communications.</li>
                            <li><strong>Legal Obligations:</strong> To comply with subpoenas, court orders, or applicable laws.</li>
                            <li><strong>With Your Consent:</strong> As explicitly authorized by you.</li>
                        </ul>
                    </section>

                    {/* Section 6 */}
                    <section className="mb-8">
                        <h3 className="text-lg font-semibold mb-3">6. How Long Do We Keep Your Information?</h3>
                        <p>
                            We retain personal information only as long as necessary for business purposes or legal compliance. When no longer needed, we securely delete or anonymize it.
                        </p>
                    </section>

                    {/* Section 7 */}
                    <section className="mb-8">
                        <h3 className="text-lg font-semibold mb-3">7. How Do We Keep Your Information Safe?</h3>
                        <p>
                            We use technical and organizational safeguards to protect your data. However, no system is 100% secure. Transmission of personal information to and from our Services is at your own risk.
                        </p>
                    </section>

                    {/* Section 8 */}
                    <section className="mb-8">
                        <h3 className="text-lg font-semibold mb-3">8. Do We Collect Information from Minors?</h3>
                        <p>
                            Our Services are not directed to individuals under 18. We do not knowingly collect information from minors. If we learn we have, we will delete it.
                        </p>
                    </section>

                    {/* Section 9 */}
                    <section className="mb-8">
                        <h3 className="text-lg font-semibold mb-3">9. What Are Your Privacy Rights?</h3>
                        <p>Depending on your location, you may have rights to:</p>
                        <ul className="mb-3 list-disc list-inside">
                            <li>Access and obtain a copy of your personal information.</li>
                            <li>Request correction or deletion.</li>
                            <li>Restrict or object to processing.</li>
                            <li>Withdraw consent at any time.</li>
                            <li>Opt out of marketing communications.</li>
                        </ul>
                    </section>

                    {/* Section 10 */}
                    <section className="mb-8">
                        <h3 className="text-lg font-semibold mb-3">10. Controls for Do-Not-Track Features</h3>
                        <p>
                            Currently, our Services do not respond to “Do Not Track” (DNT) signals due to lack of an industry standard.
                        </p>
                    </section>

                    {/* Section 11 */}
                    <section className="mb-8">
                        <h3 className="text-lg font-semibold mb-3">11. Do United States Residents Have Specific Privacy Rights?</h3>
                        <p>
                            Yes. Residents of California, Colorado, Connecticut, Utah, and Virginia may have additional rights under state privacy laws (CCPA, CPA, CTDPA, UCPA, VCDPA).
                        </p>
                        <ul className="mb-3 list-disc list-inside">
                            <li>Right to access, request, or delete data.</li>
                            <li>Right to know how data is used and shared.</li>
                            <li>Right to opt out of data sales (we do not sell personal data).</li>
                            <li>Right to non-discrimination for exercising privacy rights.</li>
                        </ul>
                        <p>
                            Requests may require verification of your identity. Authorized agents may act on your behalf, subject to proof of authorization.
                        </p>
                    </section>

                    {/* Section 12 */}
                    <section className="mb-8">
                        <h3 className="text-lg font-semibold mb-3">12. Do We Make Updates to This Notice?</h3>
                        <p>
                            Yes. We may update this Privacy Notice from time to time to stay compliant with laws and business practices. The updated date will be posted, and material changes may be notified via prominent notice or direct communication.
                        </p>
                    </section>

                    {/* Section 13 */}
                    <section className="mb-8">
                        <h3 className="text-lg font-semibold mb-3">13. How Can You Contact Us About This Notice?</h3>
                        <p>
                            If you have questions or concerns about this Privacy Notice, contact us at:
                            <br />
                            Fleety LLC
                            <br />
                            <a href="mailto:support@fleetycdl.com" className="text-blue-600 underline">
                                support@fleetycdl.com
                            </a>
                        </p>
                    </section>

                    {/* Last section */}
                    <section>
                        <h3 className="text-lg font-semibold mb-3">14. How Can You Review, Update, or Delete the Data We Collect from You?</h3>
                        <p>You may request access to, correction of, or deletion of your data at any time by submitting a data subject access request to the contact information above.</p>
                    </section>

                </div>
            </main>
        </div>

        <FooterBlue />
    </>
);

export default PrivacyPolicy;
